// src/components/private/highgarden/favorites/NearbyStores.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Container, Row, Col, ListGroup, Badge, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

const NearbyStores = ({ onStoreAdded }) => {
    const [nearbyStores, setNearbyStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasLocation, setHasLocation] = useState(false);
    const [maxDistance, setMaxDistance] = useState(10);
    const [addingStore, setAddingStore] = useState(null);
    const token = localStorage.getItem('token');

    const fetchNearbyStores = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-stores/nearby/`,
                {
                    params: {
                        max_distance_km: maxDistance,
                        limit: 20
                    },
                    headers: { 'Authorization': `Token ${token}` }
                }
            );

            setNearbyStores(response.data.results || []);
            setHasLocation(response.data.has_location);
        } catch (error) {
            if (error.response?.status === 400) {
                setHasLocation(false);
            } else {
                toast.error("Error fetching nearby stores!");
            }
        } finally {
            setLoading(false);
        }
    }, [maxDistance, token]);

    useEffect(() => {
        fetchNearbyStores();
    }, [maxDistance, fetchNearbyStores]);

    const handleAddToFavorites = async (store) => {
        setAddingStore(store.id);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-stores/`,
                { store_id: store.id },
                {
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            toast.success(`${store.name} added to favorites!`);
            // Remove from nearby list
            setNearbyStores(prev => prev.filter(s => s.id !== store.id));
            
            // Notify parent component to refresh favorites
            if (onStoreAdded) {
                onStoreAdded();
            }
        } catch (error) {
            if (error.response?.data?.store_id) {
                toast.error("This store is already in your favorites!");
            } else {
                toast.error("Error adding store to favorites!");
            }
        } finally {
            setAddingStore(null);
        }
    };

    if (loading) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Finding stores near you...</p>
            </Container>
        );
    }

    if (!hasLocation) {
        return (
            <Alert variant="info">
                <Alert.Heading>📍 Location Not Set</Alert.Heading>
                <p>
                    Please add and validate your address in your profile to see nearby stores.
                    This will help us show you the nearest available stores!
                </p>
            </Alert>
        );
    }

    return (
        <Container>
            <Row className="mb-3">
                <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5>📍 Stores Near You</h5>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Label className="mb-0">Distance:</Form.Label>
                            <Form.Select 
                                size="sm" 
                                style={{ width: 'auto' }}
                                value={maxDistance}
                                onChange={(e) => setMaxDistance(Number(e.target.value))}
                            >
                                <option value={5}>5 km</option>
                                <option value={10}>10 km</option>
                                <option value={15}>15 km</option>
                                <option value={20}>20 km</option>
                            </Form.Select>
                        </div>
                    </div>
                </Col>
            </Row>

            {nearbyStores.length === 0 ? (
                <Alert variant="warning">
                    <Alert.Heading>No Stores Found</Alert.Heading>
                    <p>
                        We couldn't find any stores within {maxDistance} km of your location.
                        Try increasing the search distance or use the search feature above to find stores manually.
                    </p>
                </Alert>
            ) : (
                <>
                    <Row className="mb-2">
                        <Col>
                            <small className="text-muted">
                                Found {nearbyStores.length} store{nearbyStores.length !== 1 ? 's' : ''} within {maxDistance} km
                            </small>
                        </Col>
                    </Row>
                    <ListGroup className="hidden-scrollbar" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {nearbyStores.map((store) => (
                            <ListGroup.Item key={store.id} className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Badge bg="none" style={{ backgroundColor: '#4b88a2', color: 'white', marginRight: '7px' }}>
                                        {store.store_brand?.name || 'Unknown Brand'}
                                    </Badge>
                                    <strong>{store.name}</strong>
                                    <div className="text-muted small">
                                        <i className="bi bi-geo-alt"></i> {store.distance_km} km away
                                        {store.address && ` • ${store.address}, ${store.city}`}
                                    </div>
                                </div>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleAddToFavorites(store)}
                                    disabled={addingStore === store.id}
                                >
                                    {addingStore === store.id ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-plus-circle"></i> Add
                                        </>
                                    )}
                                </Button>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </>
            )}
        </Container>
    );
};

export default NearbyStores;
